/**
 * Reads every ranking an item has received — its official rank in each Plan of
 * Record, its position on each agreed or proposed priority list, and its rank
 * in each session — and hands them to `buildItemRankings` for shaping.
 *
 * Three independent reads. One failing does not hide the other two: the
 * caller is told which part could not be read, so "no official rank" is never
 * shown when the truth is "could not read the official rank".
 */

import { getTypedSupabaseClient } from './typedSupabaseClient';
import { fetchPlanStatesByCycle } from './planForceService';
import { fetchPlanGoverningCycleIds } from './planGoverningCycle';
import {
  buildItemRankings,
  type ItemRanking,
  type RawOfficialRank,
  type RawPrioritySet,
  type RawSessionResult,
} from './itemRankings';

export interface ItemRankingsResult {
  rankings: ItemRanking[];
  /** Plain-language name of each part that could not be read. Empty when all were. */
  unread: Array<'ranks of record' | 'priority lists' | 'session ranks'>;
}

async function readOfficialRanks(itemId: string): Promise<RawOfficialRank[] | null> {
  const { data, error } = await getTypedSupabaseClient()
    .from('item_official_ranks')
    .select(
      'id, rank, above_the_line, plan_cycle_id, plan_of_record_snapshot_id, plan_version_id, authorized_at, authorized_by, plan_cycles(name), plan_of_record_snapshots(superseded_by_snapshot_id)',
    )
    .eq('item_id', itemId);
  if (error) return null;
  // A rank is replaced when its plan was re-authorized, or when a later plan took effect in its
  // place (migration 919: the plan reads 'replaced'). If the plan states cannot be read, the
  // re-authorization rule alone decides, as before. Which prioritization cycle each plan was
  // built from is the database's answer too (migration 843), so the item page can file the
  // plan under its cycle instead of guessing from a name.
  const planIds = (data ?? []).map((r) => r.plan_cycle_id);
  const [states, cycles] = await Promise.all([fetchPlanStatesByCycle(planIds), fetchPlanGoverningCycleIds(planIds)]);
  return (data ?? []).map((r) => ({
    id: r.id,
    rank: r.rank,
    above_the_line: r.above_the_line,
    plan_cycle_id: r.plan_cycle_id,
    authorized_at: r.authorized_at,
    authorized_by: r.authorized_by,
    plan_cycle_name: r.plan_cycles?.name ?? null,
    plan_of_record_snapshot_id: r.plan_of_record_snapshot_id ?? null,
    plan_version_id: r.plan_version_id ?? null,
    cycle_id: cycles.get(r.plan_cycle_id) ?? null,
    superseded: r.plan_of_record_snapshots?.superseded_by_snapshot_id != null || states?.get(r.plan_cycle_id)?.state === 'replaced',
    starts_later: states?.get(r.plan_cycle_id)?.state === 'starts_later',
    starts_on: states?.get(r.plan_cycle_id)?.takesEffectOn ?? null,
  }));
}

async function readPrioritySets(itemId: string): Promise<RawPrioritySet[] | null> {
  const { data, error } = await getTypedSupabaseClient()
    .from('priority_sets')
    .select('id, name, state, track_id, ranked_item_ids, cut_line_position, proposed_at, ratified_at, published_at')
    .contains('ranked_item_ids', [itemId])
    .neq('state', 'superseded');
  if (error) return null;
  return data ?? [];
}

async function readSessionResults(itemId: string): Promise<RawSessionResult[] | null> {
  const db = getTypedSupabaseClient();
  const { data: own, error } = await db
    .from('session_results')
    .select('id, session_id, rank, created_at')
    .eq('item_id', itemId);
  if (error) return null;
  const rows = own ?? [];
  if (rows.length === 0) return [];

  const sessionIds = Array.from(new Set(rows.map((r) => r.session_id)));
  // The session's name, and every rank in those sessions — the second is what
  // tells a tie ("T2") from a clear position, and gives the "of 12".
  const [{ data: sessions }, { data: all }] = await Promise.all([
    db.from('prioritization_sessions').select('id, name').in('id', sessionIds),
    db.from('session_results').select('session_id, rank').in('session_id', sessionIds).limit(10000),
  ]);

  const nameById = new Map((sessions ?? []).map((s) => [s.id, s.name]));
  const total = new Map<string, number>();
  const sameRank = new Map<string, number>();
  for (const r of all ?? []) {
    total.set(r.session_id, (total.get(r.session_id) ?? 0) + 1);
    const k = `${r.session_id}:${r.rank}`;
    sameRank.set(k, (sameRank.get(k) ?? 0) + 1);
  }

  return rows.map((r) => ({
    id: r.id,
    session_id: r.session_id,
    rank: r.rank,
    created_at: r.created_at,
    session_name: nameById.get(r.session_id) ?? null,
    ranked_count: total.get(r.session_id) ?? null,
    same_rank_count: sameRank.get(`${r.session_id}:${r.rank}`) ?? null,
  }));
}

export async function fetchItemRankings(itemId: string): Promise<ItemRankingsResult> {
  const [official, prioritySets, sessionResults] = await Promise.all([
    readOfficialRanks(itemId).catch(() => null),
    readPrioritySets(itemId).catch(() => null),
    readSessionResults(itemId).catch(() => null),
  ]);

  const unread: ItemRankingsResult['unread'] = [];
  if (official === null) unread.push('ranks of record');
  if (prioritySets === null) unread.push('priority lists');
  if (sessionResults === null) unread.push('session ranks');

  return {
    rankings: buildItemRankings(itemId, {
      official: official ?? [],
      prioritySets: prioritySets ?? [],
      sessionResults: sessionResults ?? [],
    }),
    unread,
  };
}
